import React, {
  useCallback,
  useEffect,
} from 'react'
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux'
import { Redirect } from 'react-router-dom'

import { RootState } from 'state/types'

import { AvailableInsuranceType } from 'state/quotes/types'
import { getQuoteRequestValue } from 'state/quotes/selectors'
import { setCurrentQuoteRequestEntry } from 'state/quotes/actions'

import {
  getIsInsuranceTypesCached,
  getIsInsuranceTypesLoading,
} from 'state/insuranceTypes/selectors'
import { cacheAllInsuranceTypes } from 'state/insuranceTypes/actions'

import QuotingInsuranceTypes from './index.presentational'

const QuotingInsuranceTypesContainer: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch()

  const [
    applicantCity,
    applicantFirstName,
    applicantStateAbbr,
    applicantZipCode,
    availableInsuranceTypes,
    isInsuranceTypesCached,
    isInsuranceTypesLoading,
    selectedInsuranceTypes,
  ] = useSelector((state: RootState) => [
    getQuoteRequestValue(state, 'applicant.address.city'),
    getQuoteRequestValue(state, 'applicant.personalInfo.name.firstName'),
    getQuoteRequestValue(state, 'applicant.address.stateabbr'),
    getQuoteRequestValue(state, 'applicant.address.zipCode'),
    (getQuoteRequestValue(state, 'availableInsuranceTypes') || []) as AvailableInsuranceType[],
    getIsInsuranceTypesCached(state),
    getIsInsuranceTypesLoading(state),
    getQuoteRequestValue(state, 'selectedInsuranceTypes') || {},
  ], shallowEqual)

  useEffect(() => {
    dispatch(cacheAllInsuranceTypes())
  }, [ dispatch ])

  const onChangeInsuranceType = useCallback((key: string, value: boolean) => {
    const selectedInsuranceTypes = (value && { [key]: value }) || {}

    dispatch(setCurrentQuoteRequestEntry('selectedInsuranceTypes', selectedInsuranceTypes))
  }, [ dispatch ])

  if (!applicantZipCode) {
    return React.createElement(Redirect, { to: '/quote/new/zip' })
  }

  const numSelectedAvailableInsuranceTypes =
    (availableInsuranceTypes &&
      Object.entries(selectedInsuranceTypes).filter(([
        type,
        selected,
      ]) => {
        if (type === 'other') {
          return false
        }

        return (
          (selected &&
            Object.values(availableInsuranceTypes as { slug: string }[]).find(
              availableInsuranceType => availableInsuranceType.slug === type,
            ) &&
            true) ||
          false
        )
      }).length) || 0

  const nextUrl =
    (selectedInsuranceTypes &&
      selectedInsuranceTypes.other &&
      !numSelectedAvailableInsuranceTypes &&
      '/suggest-insurance/') ||
    (selectedInsuranceTypes &&
      numSelectedAvailableInsuranceTypes &&
      '/quote/new/email/') ||
    undefined

  return React.createElement(
    QuotingInsuranceTypes,
    {
      applicantCity,
      applicantFirstName,
      applicantStateAbbr,
      availableInsuranceTypes,
      isInsuranceTypesCached,
      isInsuranceTypesLoading,
      nextUrl,
      onChangeInsuranceType,
      selectedInsuranceTypes,
    },
  )
}

export default QuotingInsuranceTypesContainer
